import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  ClipboardList,
  Handshake,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  Smartphone,
  Users,
  X,
  Zap,
} from 'lucide-react';
import React from 'react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@app/auth';
import { getContadores } from '@modules/avisos/services/avisosService';
import logoUrl from '@assets/images/italyca-logo.svg';
import usePermissions from '@shared/hooks/usePermissions';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  /** Si está definido, se renderiza un badge con ese número (omitido si = 0). */
  badge?: number;
}

const Sidebar: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { logout } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();

  // Contador de avisos pendientes — refresca cada 60s. Usa el mismo scope
  // 'mine' que la campana para que ambos badges muestren el mismo número
  // (admin y superadmin ya reciben copia de cada aviso como destinatarios,
  // así que 'mine' refleja correctamente lo que les corresponde ver).
  const { data: contadores } = useQuery({
    queryKey: ['avisos-contadores'],
    queryFn: () => getContadores(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const avisosPendientes = contadores?.total ?? 0;

  const handleLogout = () => {
    logout();
  };

  // Dashboard: solo superadmin (la matriz del cliente excluye admin y agente).
  // Usuarios: admin+ (CRUD usuarios queda fuera de agente).
  const menuItems: MenuItem[] = [
    ...(isSuperAdmin
      ? [{ path: '/dashboard-metricas', label: 'Dashboard', icon: <LayoutDashboard size={20} /> }]
      : []),
    ...(isAdmin ? [{ path: '/users', label: 'Usuarios', icon: <Users size={20} /> }] : []),
    { path: '/leads-oficina', label: 'Leads Oficina', icon: <Building2 size={20} /> },
    { path: '/leads-digitales', label: 'Leads Digitales', icon: <Smartphone size={20} /> },
    {
      path: '/prospeccion-agentes',
      label: 'Prospección Agentes',
      icon: <ClipboardList size={20} />,
    },
    { path: '/inmuebles', label: 'Inmuebles', icon: <Home size={20} /> },
    { path: '/operaciones', label: 'Operaciones', icon: <Handshake size={20} /> },
    { path: '/cambio-suministros', label: 'Suministros', icon: <Zap size={20} /> },
    { path: '/seguro-alquiler', label: 'Seguro Impago', icon: <ShieldCheck size={20} /> },
    { path: '/avisos', label: 'Avisos', icon: <Bell size={20} />, badge: avisosPendientes },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
        aria-expanded={isOpen}
        aria-controls="sidebar-nav"
        className="fixed top-4 left-4 z-50 p-2 bg-brand-dark text-white rounded-lg lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tomato/50"
      >
        {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
      </button>

      <aside
        id="sidebar-nav"
        aria-label="Navegación principal"
        className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-brand-dark text-white transition-transform duration-300 ease-in-out transform
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen
      `}
      >
        <div className="flex flex-col h-full">
          <div className="h-16 p-6 border-b border-brand-silver/10 bg-white">
            <img
              src={logoUrl}
              alt="Italyca Inmobiliaria"
              className="h-6 w-auto mx-auto"
              width="120"
              height="24"
            />
          </div>
          {/* `min-h-0` permite que el flex-child se encoja por debajo de su
              contenido (sin esto, `overflow-y-auto` no se activa nunca);
              `overscroll-contain` evita que el scroll del menú propague al
              body cuando llegas al borde. Logo y "Cerrar Sesión" quedan
              siempre fijos arriba y abajo; solo los items navegan. */}
          <nav className="sidebar-nav-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain py-4 px-3 space-y-1">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center px-4 py-3 rounded-lg transition-all duration-200 group
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tomato/50
                  ${
                    isActive
                      ? 'bg-brand-tomato text-white'
                      : 'text-brand-light hover:bg-white/5 hover:text-white'
                  }
                `}
                onClick={() => setIsOpen(false)}
              >
                <span className="mr-3" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="font-medium flex-1">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span
                    aria-label={`${item.badge} pendientes`}
                    className="ml-2 min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[10px] font-bold text-white bg-brand-tomato rounded-full"
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-brand-silver/10">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-brand-light hover:bg-white/5 hover:text-white rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tomato/50"
            >
              <LogOut size={20} className="mr-3" aria-hidden="true" />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
            <div className="mt-4 text-xs text-brand-light text-center">
              &copy; {new Date().getFullYear()} Italyca ERP
            </div>
          </div>
        </div>
      </aside>

      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}
    </>
  );
};

export default Sidebar;
